import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { UsersRound, Loader2, RefreshCw, GraduationCap, Users, ShieldCheck, Network } from 'lucide-react';

const MODULES = [
  { key: 'orientation', label: 'Orientation & Training', icon: GraduationCap, color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  { key: 'staff',       label: 'Staff',                 icon: Users,          color: 'text-blue-700 bg-blue-50 border-blue-200' },
  { key: 'credentials', label: 'Credentials',           icon: ShieldCheck,    color: 'text-amber-700 bg-amber-50 border-amber-200' },
  { key: 'staffing',    label: 'Staffing',              icon: Network,        color: 'text-purple-700 bg-purple-50 border-purple-200' },
];

const ROLE_BADGE = {
  administrator: 'bg-purple-100 text-purple-800',
  supervisor: 'bg-blue-100 text-blue-800',
  scheduler: 'bg-yellow-100 text-yellow-800',
  hr: 'bg-green-100 text-green-800',
  staff: 'bg-gray-100 text-gray-700',
  employee: 'bg-gray-100 text-gray-700',
};

/**
 * HR Access Manager — one row per user, four independent toggles.
 * Administrators are locked-on and read-only (they always have access).
 * A grant/revoke click hits PUT /api/admin/hr-access/{user_id} with
 * {module, grant} and optimistically updates the row.
 */
export default function HrAccessManager({ api }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});     // { "userId:module": true }
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/hr-access');
      setUsers(data || []);
    } catch (err) {
      toast.error(err.response?.status === 403 ? 'Administrators only' : 'Failed to load HR access');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (u, moduleKey, currentValue) => {
    if (u.is_admin) return;    // admins are locked-on
    const key = `${u.id}:${moduleKey}`;
    setBusy(b => ({ ...b, [key]: true }));
    try {
      await api.put(`/admin/hr-access/${u.id}`, { module: moduleKey, grant: !currentValue });
      setUsers(prev => prev.map(x =>
        x.id === u.id ? { ...x, modules: { ...x.modules, [moduleKey]: !currentValue } } : x
      ));
      toast.success(`${u.name || u.email}: ${moduleKey} ${!currentValue ? 'granted' : 'revoked'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
    setBusy(b => { const n = { ...b }; delete n[key]; return n; });
  };

  const filtered = users.filter(u => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (u.name || '').toLowerCase().includes(s)
        || (u.email || '').toLowerCase().includes(s)
        || (u.role || '').toLowerCase().includes(s);
  });

  if (loading) return <Card><CardContent className="p-12 text-center"><Loader2 className="h-7 w-7 animate-spin text-[#6B3FA0] mx-auto" /></CardContent></Card>;

  return (
    <Card data-testid="hr-access-manager">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-[#EDE7F6]">
              <UsersRound className="h-5 w-5 text-[#6B3FA0]" />
            </div>
            <div>
              <CardTitle className="text-base font-bold">HR — module access</CardTitle>
              <p className="text-xs text-gray-500 mt-0.5 max-w-2xl">
                Grant individual staff access to Orientation, Staff, Credentials, or Staffing without promoting their role.
                Administrators and supervisors always have access.
              </p>
            </div>
          </div>
          <Button onClick={load} variant="outline" size="sm" className="gap-1.5" data-testid="hr-access-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <input
          type="text"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Filter by name, email, or role…"
          className="w-full max-w-md mb-4 border rounded px-3 py-1.5 text-sm"
          data-testid="hr-access-search"
        />

        <div className="overflow-x-auto -mx-4 md:mx-0">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-gray-500 border-b">
              <tr>
                <th className="text-left p-2 pl-4">User</th>
                {MODULES.map(m => (
                  <th key={m.key} className="text-center p-2 min-w-[110px]">
                    <div className="flex items-center justify-center gap-1">
                      <m.icon className="h-3.5 w-3.5" /> {m.label.split(' ')[0]}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-gray-500 py-6">No matching users.</td></tr>
              ) : filtered.map(u => (
                <tr key={u.id} className="border-b hover:bg-gray-50" data-testid={`hr-access-row-${u.id}`}>
                  <td className="p-2 pl-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{u.name || '(no name)'}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {u.email}
                          <Badge className={`ml-2 text-[9px] ${ROLE_BADGE[u.role] || 'bg-gray-100 text-gray-700'} border-0`}>
                            {u.role || 'staff'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </td>
                  {MODULES.map(m => {
                    const val = !!u.modules[m.key];
                    const busyKey = `${u.id}:${m.key}`;
                    const isBusy = !!busy[busyKey];
                    return (
                      <td key={m.key} className="p-2 text-center">
                        {u.is_admin ? (
                          <Badge className="bg-purple-100 text-purple-800 border-0 text-[10px]" title="Admins always have access">admin</Badge>
                        ) : (
                          <button
                            onClick={() => toggle(u, m.key, val)}
                            disabled={isBusy}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${val ? 'bg-[#6B3FA0]' : 'bg-gray-300'} ${isBusy ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
                            aria-label={`${val ? 'Revoke' : 'Grant'} ${m.label} access to ${u.name}`}
                            data-testid={`hr-toggle-${u.id}-${m.key}`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${val ? 'translate-x-6' : 'translate-x-1'}`}
                            />
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
